import { useState, useEffect } from "react";
import { X, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useFacebookPixel } from "@/hooks/useFacebookPixel";
import { useUserIp } from "@/hooks/useUserIp";
import useUtmParams from "@/hooks/useUtmParams";
import { useClarityTracking } from "@/hooks/useClarityTracking";
import { getFacebookTracking } from "@/hooks/useFacebookTracking";
import { useNavigate } from "react-router-dom";

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
}

interface VideoFormOverlayProps {
  isVisible: boolean;
  hasEnded: boolean;
  onClose: () => void;
  onResume: () => void;
  isInModal?: boolean;
}

const VideoFormOverlay = ({ isVisible, hasEnded, onClose, onResume, isInModal = false }: VideoFormOverlayProps) => {
  const [step, setStep] = useState(1);
  const [isDisqualified, setIsDisqualified] = useState(false);
  const [formData, setFormData] = useState({
    dealVolume: "",
    goal: "",
    fullName: "",
    email: "",
    phone: "",
  });
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(() => 
    localStorage.getItem('videoFormSubmitted') === 'true'
  );

  const { trackLead } = useFacebookPixel();
  const userIp = useUserIp();
  const utmParams = useUtmParams();
  const { trackFormStep, trackDealVolume, trackBudget, trackFormSubmit, trackDisqualified } = useClarityTracking();
  const navigate = useNavigate();

  const dealVolumeOptions = [
    "Just starting / 0-1 Deals",
    "Scaling / 2-5 Deals",
    "Pro / 5+ Deals",
  ];

  const budgetOptions = [
    "Less than $3k/mo",
    "$3k - $10k/mo",
    "$10k+/mo",
  ];

  // Check if already submitted on mount
  useEffect(() => {
    const submitted = localStorage.getItem('videoFormSubmitted') === 'true';
    setHasSubmitted(submitted);
  }, []);

  // Reset form when overlay becomes visible
  useEffect(() => {
    if (isVisible && !hasSubmitted) {
      setStep(1);
      setIsDisqualified(false);
    }
  }, [isVisible, hasSubmitted]);

  const handleDealVolumeSelect = (option: string) => {
    setFormData({ ...formData, dealVolume: option });
    trackDealVolume(option);
    setStep(2);
    trackFormStep(2, 'video_overlay');
  };

  const handleGoalSelect = (option: string) => {
    setFormData({ ...formData, goal: option });
    trackBudget(option);
    if (option === "Less than $3k/mo") {
      setIsDisqualified(true);
      trackDisqualified('low_budget');
    } else {
      setStep(3);
      trackFormStep(3, 'video_overlay');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = "Name must be at least 2 characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    const phoneRegex = /^[\d\s\-\+\(\)]{10,}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    trackFormSubmit('video_overlay');
    
    try {
      // Get Facebook tracking from first-party storage (bypasses ad blockers)
      const { fbc, fbp } = getFacebookTracking();
      const event_source_url = window.location.href;
      console.log('Video Form - First-party Facebook tracking:', { fbc, fbp, event_source_url });

      // Insert into Supabase
      const { error } = await supabase.from("leads").insert({
        full_name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        deal_volume: formData.dealVolume,
        marketing_budget: formData.goal,
        source: "video_overlay",
        ip_address: userIp,
        utm_source: utmParams?.utm_source,
        utm_medium: utmParams?.utm_medium,
        utm_campaign: utmParams?.utm_campaign,
        utm_term: utmParams?.utm_term,
        utm_content: utmParams?.utm_content,
        gclid: utmParams?.gclid,
        fbclid: utmParams?.fbclid,
        msclkid: utmParams?.msclkid,
        landing_page: utmParams?.landing_page,
        referrer: utmParams?.referrer,
        fbc,
        fbp,
        event_source_url,
      });

      if (error) throw error;

      // Track Lead event with Facebook Pixel & Conversion API
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      trackLead({
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        firstName,
        lastName,
        clientIpAddress: userIp || undefined,
      });

      // Send to Zapier webhook via edge function
      try {
        const { error: zapierError } = await supabase.functions.invoke('zapier-webhook', {
          body: {
            full_name: formData.fullName.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            deal_volume: formData.dealVolume,
            marketing_budget: formData.goal,
            source: "video_overlay",
            ip_address: userIp,
          },
        });
        if (zapierError) {
          console.error("Zapier webhook error:", zapierError);
        }
      } catch (zapierError) {
        console.error("Zapier webhook error:", zapierError);
      }

      // Mark as submitted
      localStorage.setItem('videoFormSubmitted', 'true');
      setHasSubmitted(true);

      // Navigate to booking page with params
      const params = new URLSearchParams({
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        name: formData.fullName.trim(),
      });
      navigate(`/book?${params.toString()}`);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setIsDisqualified(false);
    setStep(1);
    setFormData({ dealVolume: "", goal: "", fullName: "", email: "", phone: "" });
  };

  // Don't render if already submitted or not visible
  if (hasSubmitted || !isVisible) return null;

  const renderDisqualified = () => (
    <div className="text-center py-2">
      <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-muted flex items-center justify-center">
        <span className="text-lg">🙏</span>
      </div>
      <h3 className="text-foreground text-base font-medium mb-1">Thanks for your interest!</h3>
      <p className="text-muted-foreground text-xs mb-3 leading-relaxed">
        Our program is for investors actively closing deals with a marketing budget to scale.
      </p>
      <button
        onClick={handleStartOver}
        className="text-xs text-orange-500 hover:text-orange-400 transition-colors min-h-[44px] px-4"
      >
        ← Start Over
      </button>
    </div>
  );

  const renderStep1 = () => (
    <div>
      <h3 className="text-foreground text-sm sm:text-base font-medium mb-2">Current Deal Volume?</h3>
      <div className="space-y-1">
        {dealVolumeOptions.map((option, index) => (
          <button
            key={index}
            onClick={() => handleDealVolumeSelect(option)}
            className="w-full group flex items-center justify-between p-2 sm:p-3 rounded-lg bg-secondary/50 hover:bg-secondary border border-border/50 hover:border-orange-500/30 transition-all text-left min-h-[40px] sm:min-h-[44px]"
          >
            <span className="text-xs text-muted-foreground group-hover:text-foreground font-medium">
              {option}
            </span>
            <span className="text-muted-foreground group-hover:text-orange-500">→</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 className="text-foreground text-sm sm:text-base font-medium mb-2">Marketing Budget?</h3>
      <div className="space-y-1">
        {budgetOptions.map((option, index) => (
          <button
            key={index}
            onClick={() => handleGoalSelect(option)}
            className="w-full group flex items-center justify-between p-2 sm:p-3 rounded-lg bg-secondary/50 hover:bg-secondary border border-border/50 hover:border-orange-500/30 transition-all text-left min-h-[40px] sm:min-h-[44px]"
          >
            <span className="text-xs text-muted-foreground group-hover:text-foreground font-medium">
              {option}
            </span>
            <span className="text-muted-foreground group-hover:text-orange-500">→</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <form onSubmit={handleSubmit}>
      <h3 className="text-foreground text-base font-medium mb-2">Enter your details</h3>
      <div className="space-y-2">
        {/* Honeypot */}
        <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Full Name */}
        <div>
          <input
            type="text"
            placeholder="Full Name"
            value={formData.fullName}
            onChange={(e) => {
              setFormData({ ...formData, fullName: e.target.value });
              if (errors.fullName) setErrors({ ...errors, fullName: undefined });
            }}
            className={`w-full p-2.5 rounded-lg bg-secondary/50 border ${errors.fullName ? 'border-destructive/50' : 'border-border/50'} focus:border-orange-500/50 focus:outline-none text-sm text-foreground placeholder:text-muted-foreground transition-colors`}
          />
          {errors.fullName && <p className="text-destructive text-xs mt-0.5 text-left">{errors.fullName}</p>}
        </div>

        {/* Email */}
        <div>
          <input
            type="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (errors.email) setErrors({ ...errors, email: undefined });
            }}
            className={`w-full p-2.5 rounded-lg bg-secondary/50 border ${errors.email ? 'border-destructive/50' : 'border-border/50'} focus:border-orange-500/50 focus:outline-none text-sm text-foreground placeholder:text-muted-foreground transition-colors`}
          />
          {errors.email && <p className="text-destructive text-xs mt-0.5 text-left">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <input
            type="tel"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={(e) => {
              setFormData({ ...formData, phone: e.target.value });
              if (errors.phone) setErrors({ ...errors, phone: undefined });
            }}
            className={`w-full p-2.5 rounded-lg bg-secondary/50 border ${errors.phone ? 'border-destructive/50' : 'border-border/50'} focus:border-orange-500/50 focus:outline-none text-sm text-foreground placeholder:text-muted-foreground transition-colors`}
          />
          {errors.phone && <p className="text-destructive text-xs mt-0.5 text-left">{errors.phone}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full p-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-70 text-white font-medium text-sm transition-colors flex items-center justify-center gap-2 min-h-[44px]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Book My Strategy Call →"
          )}
        </button>
      </div>
    </form>
  );

  const renderStep = () => {
    if (isDisqualified) return renderDisqualified();
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      default: return null;
    }
  };

  return (
    <div className={isInModal 
      ? "relative p-4" 
      : "absolute inset-0 z-20 flex items-center justify-center bg-background/95 backdrop-blur-sm animate-fade-in p-2"
    }>
      <div className={`w-full ${isInModal ? 'max-w-full' : 'max-w-[280px]'} px-3 py-2 sm:py-3`}>
        {/* Compact Header Row */}
        <div className="flex items-center justify-between mb-2">
          {/* Back button (steps 2 & 3) or spacer */}
          {step > 1 && !isDisqualified ? (
            <button
              onClick={() => setStep(step - 1)}
              className="p-1.5 rounded-full hover:bg-muted/50 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            </button>
          ) : (
            <div className="w-8" />
          )}

          {/* Step indicator */}
          {!isDisqualified && (
            <div className="text-[10px] font-mono text-muted-foreground">
              STEP {step}/3
            </div>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-muted/50 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center"
            aria-label="Close form"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Step Content */}
        {renderStep()}

        {/* Continue Watching - compact */}
        <button
          onClick={onResume}
          className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-1.5"
        >
          Continue watching →
        </button>
      </div>
    </div>
  );
};

export default VideoFormOverlay;
